<template>
  <AdminLayout>
    <v-row>
      <v-col cols="12">
        <v-alert
          class="custom-alert"
          v-if="!bookableCountCheck"
          type="info"
          elevation="2"
        >
          Sie haben die maximale Anzahl an öffentlichen Buchungsobjekten
          erreicht. Erweitern Sie Ihr Kontingent, oder löschen Sie nicht mehr
          benötigte Buchungsobjekte.
        </v-alert>
      </v-col>
    </v-row>
    <BookableSearchAndFilter
      type="Tickets"
      @update:search="searchQuery = $event"
      @update:filters="filters = $event"
    />
    <v-row class="align-stretch pb-15">
      <v-col
        cols="12"
        sm="6"
        md="4"
        lg="3"
        v-for="ticket in filteredTickets.slice().reverse()"
        :key="ticket.id"
        class="d-flex"
      >
        <v-skeleton-loader type="card" class="flex" :loading="loading">
          <bookable-card
            :item="ticket"
            editRoute="ticket-edit"
            :from-route="$router.currentRoute.value.name"
            @delete="removeBookable(ticket.id)"
            @duplicate="duplicateBookable(ticket.id)"
          ></bookable-card>
        </v-skeleton-loader>
      </v-col>
    </v-row>
    <v-fab-transition>
      <v-btn
        color="primary"
        fixed
        size="large"
        rounded
        class="position-fixed bottom-0 right-0 mb-4 mr-5"
        :to="{
          name: 'ticket-edit',
          query: { fromRoute: $router.currentRoute.value.name },
        }"
        :disabled="createDisabled"
      >
        <v-icon>mdi-plus</v-icon>
        Ticket Erstellen
      </v-btn>
    </v-fab-transition>
  </AdminLayout>
</template>

<script>
import AdminLayout from "@/layouts/Admin.vue";
import { mapGetters, mapActions } from "vuex";
import BookableCard from "@/components/BookableCard.vue";
import BookableSearchAndFilter from "@/components/Filters/BookableSearchAndFilter.vue";
import ApiBookablesService from "@/services/api/ApiBookablesService";
import ToastService from "@/services/ToastService";
import BookablePermissionService from "@/services/permissions/BookablePermissionService";

export default {
  components: {
    AdminLayout,
    BookableCard,
    BookableSearchAndFilter,
  },
  data() {
    return {
      api: {
        tickets: [],
        tags: [],
      },
      filters: {},
      searchQuery: "",
      bookableCountCheck: true,
    };
  },
  computed: {
    ...mapGetters({
      loading: "loading/isLoading",
      tenantId: "tenants/currentTenantId",
    }),
    createDisabled() {
      return (
        !this.BookablePermissionService.allowCreate() ||
        !this.bookableCountCheck
      );
    },
    BookablePermissionService() {
      return BookablePermissionService;
    },
    filteredTickets() {
      const tickets = this.api.tickets || [];
      const f = this.filters;

      const hasFilter =
        this.searchQuery ||
        (f.selectedTags && f.selectedTags.length > 0) ||
        f.isBookable !== null ||
        f.isPublic !== null ||
        f.allowRecurringBooking !== null ||
        f.autoCommitBooking !== null;

      if (!hasFilter) return tickets;

      return tickets.filter((ticket) => {
        if (this.searchQuery) {
          const query = this.searchQuery.toLowerCase();
          const matchesSearch =
            (ticket.title && ticket.title.toLowerCase().includes(query)) ||
            (ticket.description &&
              ticket.description.toLowerCase().includes(query)) ||
            (ticket.location && ticket.location.toLowerCase().includes(query));
          if (!matchesSearch) return false;
        }
        if (f.selectedTags && f.selectedTags.length > 0) {
          const hasAllTags = f.selectedTags.every((tag) =>
            ticket.tags?.includes(tag)
          );
          if (!hasAllTags) return false;
        }
        if (f.isBookable !== null && ticket.isBookable !== f.isBookable)
          return false;
        if (f.isPublic !== null && ticket.isPublic !== f.isPublic) return false;
        if (
          f.allowRecurringBooking !== null &&
          ticket.allowRecurringBooking !== f.allowRecurringBooking
        )
          return false;
        if (
          f.autoCommitBooking !== null &&
          ticket.autoCommitBooking !== f.autoCommitBooking
        )
          return false;

        return true;
      });
    },
  },
  watch: {
    tenantId() {
      this.fetchTickets();
    },
  },
  methods: {
    ...mapActions({
      startLoading: "loading/start",
      stopLoading: "loading/stop",
      addToast: "toasts/add",
    }),
    remove(item) {
      this.filters.splice(this.filters.indexOf(item), 1);
    },
    async removeBookable(bookableId) {
      await this.startLoading("fetch-delete-bookable");
      ApiBookablesService.deleteBookable(bookableId)
        .then(() => {
          this.fetchTickets();
        })
        .finally(() => {
          this.addToast(
            ToastService.createToast("bookable.delete.success", "success")
          );
          this.stopLoading("fetch-delete-bookable");
        })
        .catch((error) => {
          this.addToast(
            ToastService.createToast("errors.something-wrong", "error")
          );
          console.log(error);
        });
      await this.getBookableCount();
    },
    async duplicateBookable(bookableId) {
      await this.startLoading("fetch-duplicate-bookable");

      ApiBookablesService.duplicateBookable(bookableId)
        .then(() => {
          this.fetchTickets();
        })
        .finally(() => {
          this.addToast(
            ToastService.createToast("bookable.duplicate.success", "success")
          );
          this.stopLoading("fetch-duplicate-bookable");
        })
        .catch((error) => {
          this.addToast(
            ToastService.createToast(
              "bookable.duplicate.errors.something-wrong",
              "error"
            )
          );
          console.log(error);
        });
      await this.getBookableCount();
    },
    fetchTickets() {
      this.startLoading("fetch-tickets");
      ApiBookablesService.getBookables(undefined, true)
        .then((response) => {
          this.api.tickets = response.data.filter(
            (resource) => resource.type === "ticket"
          );
        })
        .finally(() => {
          this.stopLoading("fetch-tickets");
        })
        .catch((error) => {
          console.log(error);
        });
    },
    async getBookableCount() {
      this.bookableCountCheck =
        await ApiBookablesService.publicBookableCountCheck();
    },
  },
  async mounted() {
    await this.getBookableCount();
  },
  created() {
    this.fetchTickets();
  },
};
</script>
<style scoped>
.filter-field {
  border-radius: 15px;
}
.custom-alert {
  border-radius: 15px !important;
}
</style>
